import React, { useEffect, useState } from 'react';

import type { CreateTarget } from 'actions';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { createQueue, updateQueue } from '../../../../redux/slices/lists/queuesSlice';
import { fetchConnectionsPaginated } from '../../../../redux/slices/connectionsSlice';
import { getWorkersPaginated } from '../../../../redux/slices/workersSlice';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';

import CreateQueueModal, { CreateQueueFormState } from './CreateQueueModal';
import EditQueueModal, { EditQueueFormState } from './EditQueueModal';
import type { FetchedQueue } from 'api/queues/endpoints/getQueue';
import { DEFAULT_QUEUE_ALLOCATION_STATUSES, QUEUE_ALLOCATION_STATUS_OPTIONS } from './queueAllocationStatuses';

const buildEmptyCreate = (): CreateQueueFormState => ({
  queue_name: '',
  queue_limit: 0,
  queue_status: 'Open',
  zone_name: 'Root',
  queue_description: '',
  workers: [],
  connections: [],
  scheduling_window_id: null,
  queue_allocation_statuses: [...DEFAULT_QUEUE_ALLOCATION_STATUSES],
  folder_id: null,
});

// Exported for test: every field it produces is sent back in the update body, so the null handling here decides what
// gets WRITTEN for a row whose nullable columns are unset — not just what the form displays.
export const buildEditFromRow = (row: FetchedQueue): EditQueueFormState => ({
  queue_id: row.queue_id,
  // `queues.queue_name` / `queue_status` are nullable columns. `?? ''` is what the form needs (a text input and a select
  // both want a string) and is also exactly what the old `str()` declaration substituted, so a null row edits and saves
  // the same as before. Deliberately NOT defaulted to 'Open': that would turn an unset status into a real one on save.
  queue_name: row.queue_name ?? '',
  queue_new_name: '',
  queue_limit: row.queue_limit ?? 0,
  queue_status: row.queue_status ?? '',
  zone_name: row.zone_name ?? '',
  zone_new_name: '',
  queue_description: row.queue_description ?? '',
  // `queues_workers.priority` is nullable on the wire but the form state types it `number` and binds it to a number
  // input. 0 is both what the old `num()` declaration substituted and what the "add a worker" path seeds, so an unset
  // priority edits exactly as it did before rather than as an uncontrolled input.
  workers: Array.isArray(row.workers) ? row.workers.map((w) => ({ ...w, priority: w.priority ?? 0 })) : [],
  connections: Array.isArray(row.connections) ? row.connections : [],
  scheduling_window_id: row.scheduling_window_id ?? null,
  queue_allocation_statuses: Array.isArray(row.queue_allocation_statuses) ? row.queue_allocation_statuses : [...DEFAULT_QUEUE_ALLOCATION_STATUSES],
  folder_id: row.folder_id ?? null,
});

export interface QueueEditDialogProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  data: FetchedQueue | null;
  // Workspace create pre-target: seeds the zone + folder on create.
  createIn?: CreateTarget;
}

/**
 * Stateful container for the queue create/edit flow. Owns the form state,
 * self-fetches the zone / connection / worker LOVs, and persists via the queue
 * thunks — so the presentational Create/EditQueueModal can be opened from
 * anywhere, not just the list page.
 */
const QueueEditDialog: React.FC<QueueEditDialogProps> = ({ show, handleClose, mode, data, createIn }) => {
  const dispatch = useAppDispatch();
  const { zones } = useAppSelector((state) => state.listOfZones);
  const connections = useAppSelector((state) => state.connections.items);
  const workers = useAppSelector((state) => state.workers.items);

  const [createForm, setCreateForm] = useState<CreateQueueFormState>(buildEmptyCreate());
  const [editForm, setEditForm] = useState<EditQueueFormState | null>(null);

  // Initialise the form whenever the dialog opens.
  useEffect(() => {
    if (!show) return;
    if (mode === 'edit' && data) {
      setEditForm(buildEditFromRow(data));
    } else {
      setCreateForm({ ...buildEmptyCreate(), ...(createIn ? { zone_name: createIn.zoneName, folder_id: createIn.folderId } : {}) });
    }
  }, [show, mode, data, createIn]);

  // Fetch the reference data the form needs.
  useEffect(() => {
    if (!show) return;
    dispatch(getAuthorizedZones({ resourceType: 'queues', action: mode === 'edit' ? 'update' : 'create' }) as any);
    dispatch(fetchConnectionsPaginated({}) as any);
    dispatch(getWorkersPaginated({}) as any);
  }, [dispatch, show, mode]);

  const handleSaveCreate = async (form: CreateQueueFormState) => {
    const action: any = await dispatch(
      createQueue({
        queue_name: form.queue_name,
        queue_status: form.queue_status,
        zone_name: form.zone_name,
        queue_limit: Number(form.queue_limit) || 0,
        queue_description: form.queue_description,
        workers: form.workers,
        connections: form.connections,
        scheduling_window_id: form.scheduling_window_id,
        queue_allocation_statuses: form.queue_allocation_statuses,
        folder_id: form.folder_id,
      }) as any
    );
    if (createQueue.fulfilled.match(action)) handleClose();
  };

  const handleSaveEdit = async (form: EditQueueFormState) => {
    const action: any = await dispatch(
      updateQueue({
        queue_id: form.queue_id,
        queue_name: form.queue_name,
        queue_new_name: form.queue_new_name,
        queue_limit: Number(form.queue_limit) || 0,
        zone_new_name: form.zone_new_name,
        queue_status: form.queue_status,
        zone_name: form.zone_name,
        queue_description: form.queue_description,
        workers: form.workers,
        connections: form.connections,
        scheduling_window_id: form.scheduling_window_id,
        queue_allocation_statuses: form.queue_allocation_statuses,
        folder_id: form.folder_id,
      }) as any
    );
    if (updateQueue.fulfilled.match(action)) handleClose();
  };

  // TODO #757 step 3 PR D: align Zone shape with the modal's prop shape
  const zonesProp = zones as unknown as { id: string; name: string }[];

  if (mode === 'edit') {
    return (
      <EditQueueModal
        show={show}
        value={editForm}
        zones={zonesProp}
        connections={connections}
        workers={workers}
        allocationStatusOptions={[...QUEUE_ALLOCATION_STATUS_OPTIONS]}
        onChange={setEditForm}
        onClose={handleClose}
        onSave={handleSaveEdit}
      />
    );
  }

  return (
    <CreateQueueModal
      show={show}
      value={createForm}
      zones={zonesProp}
      connections={connections}
      workers={workers}
      allocationStatusOptions={[...QUEUE_ALLOCATION_STATUS_OPTIONS]}
      onChange={setCreateForm}
      onClose={handleClose}
      onSave={handleSaveCreate}
    />
  );
};

export default QueueEditDialog;
